/**
 * svgRenderTop — mutations for the 3D (render_top) viewer variant.
 *
 * Applied by renderers on a *clone* of the cached SVGElement — never on the
 * cached element itself. Operations:
 *   1. Hide 2D-only layers (equipment, openings, tags, stairways, railings, walls).
 *   2. Set all `path.compartment` fills to the injected <pattern> for PNG texture.
 *   3. Add a single <image> underlay (no tiling → no edge seams).
 */

// ── 3D variant (render_top) mutations ────────────────────────────────────────

/**
 * Adds a background <image> with the PNG texture as the first child of the
 * `.apartment_tier` group (below all other content in the group).
 *
 * Uses a single <image> rather than a <rect fill="url(#pattern)"> so the PNG
 * is rendered exactly once — no tile boundary, no edge seams.
 *
 * The <image> sits inside .apartment_tier (transform="scale(1,-1)") with its
 * own scale(1,-1) and y=-vbH to match the CAD bottom-up convention, exactly
 * like the image inside the pattern definition.
 *
 * @param {SVGElement} svgElement — clone to mutate in place
 * @param {string}     pngUrl     — absolute URL of the floor plan PNG
 */
function _addUnderlayImage(svgElement, pngUrl) {
  const svgNS = 'http://www.w3.org/2000/svg'

  // Remove any previously injected underlay (idempotent)
  svgElement.querySelector('[data-layer="underlay"]')?.remove()

  const viewBox = (svgElement.getAttribute('viewBox') ?? '').split(/\s+/).map(Number)
  const [, , vbW = 1000, vbH = 1000] = viewBox

  const underlayGroup = document.createElementNS(svgNS, 'g')
  underlayGroup.setAttribute('data-layer', 'underlay')
  underlayGroup.setAttribute('pointer-events', 'none')

  const image = document.createElementNS(svgNS, 'image')
  image.setAttribute('href', pngUrl)
  // Same flip trick as inside the pattern (PNG is bottom-up CAD).
  image.setAttribute('x', '0')
  image.setAttribute('y', String(-vbH))
  image.setAttribute('width', String(vbW))
  image.setAttribute('height', String(vbH))
  image.setAttribute('transform', 'scale(1, -1)')
  image.setAttribute('preserveAspectRatio', 'none')

  underlayGroup.appendChild(image)

  // Insert inside .apartment_tier as first child — inherits the group's transform
  const apartmentTier = svgElement.querySelector('.apartment_tier')
  if (apartmentTier) {
    apartmentTier.insertBefore(underlayGroup, apartmentTier.firstChild)
  } else {
    // Fallback: insert at SVG root if structure is unexpected
    svgElement.insertBefore(underlayGroup, svgElement.firstChild)
  }
}

/**
 * CSS classes / selectors for elements hidden in the 3D (render_top) variant.
 *
 * These layers are specific to the 2D orthographic presentation (equipment,
 * openings, tags, etc.) and are replaced visually by the PNG texture fill.
 *
 * Multi-class elements like "casework kitchenette" are matched with ~= (word boundary).
 */
const RENDER_TOP_HIDDEN_SELECTORS = [
  '.equipments',
  '[class~="casework"]',
  '.tag',
  '.tags',
  '.stairways',
  '.openings_door',
  '.openings_window',
  '.railings',
  '.walls',
]

/**
 * applyRenderTopMutations — mutates a cloned SVGElement to render the 3D (render_top) variant.
 *
 * Called by each renderer on its own clone — NEVER on the cached canonical element.
 *
 * Operations:
 *   1. Hides 2D-only layers (equipment, openings, tags, stairways, railings, walls).
 *   2. Sets all `path.compartment` fills to the named pattern so the PNG texture shows.
 *
 * The `patternId` option is intentionally exposed for V2 style-switching:
 * multiple patterns can be pre-injected into <defs> and selected here by id.
 *
 * The `keepWindows` option preserves the `.openings_window` layer — required by
 * InsolationMode, which paints sun-patch overlays anchored to window geometry
 * even in the 3D variant.
 *
 * @param {SVGElement} svgElement — clone to mutate in place
 * @param {object}     [options]
 * @param {string}     [options.patternId='treeDStyle-default'] — id of the <pattern> to use
 * @param {boolean}    [options.keepWindows=false] — when true, do not hide `.openings_window`
 */
export function applyRenderTopMutations(svgElement, options = {}) {
  const { patternId = 'treeDStyle-default', keepWindows = false } = options

  // Hide 2D-only layers (optionally preserving windows for InsolationMode)
  const hiddenSelectors = keepWindows
    ? RENDER_TOP_HIDDEN_SELECTORS.filter((s) => s !== '.openings_window')
    : RENDER_TOP_HIDDEN_SELECTORS
  for (const selector of hiddenSelectors) {
    const elements = svgElement.querySelectorAll(selector)
    for (const el of elements) {
      el.style.display = 'none'
    }
  }

  // Apply PNG texture fill to all compartments
  const compartments = svgElement.querySelectorAll('path.compartment')
  for (const path of compartments) {
    path.style.fill = `url(#${patternId})`
  }

  // Insert single <image> underlay (no tiling → no edge seams).
  // We re-use the PNG URL from the existing <pattern> image so the API stays
  // unchanged at the call site.
  const patternImage = svgElement.querySelector(`#${patternId} image`)
  const pngUrl = patternImage?.getAttribute('href')
  if (pngUrl) {
    _addUnderlayImage(svgElement, pngUrl)
  }
}
